
import { useState, useMemo, useRef } from 'react'
import { ChevronsUpDown, Search } from 'lucide-react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

const ITEM_HEIGHT = 44

interface User {
  _id?: string
  id?: string
  name: string
  surname?: string
  email: string
}

interface UserPickerProps {
  users: User[]
  value: string
  onValueChange: (userId: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function UserPicker({
  users,
  value,
  onValueChange,
  placeholder = 'Choose a user...',
  disabled = false,
  className,
}: UserPickerProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users
    const q = search.toLowerCase().trim()
    return users.filter(
      (u) =>
        u.name?.toLowerCase().includes(q) ||
        u.surname?.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
    )
  }, [users, search])

  const selectedUser = useMemo(
    () => users.find((u) => (u._id || u.id) === value),
    [users, value]
  )

  const rowVirtualizer = useVirtualizer({
    count: filteredUsers.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 5,
  })

  const handleSelect = (userId: string) => {
    onValueChange(userId)
    setOpen(false)
    setSearch('')
  }

  const displayLabel = selectedUser
    ? `${selectedUser.name} ${selectedUser.surname || ''} (${selectedUser.email})`.trim()
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
              onKeyDown={(e) => e.stopPropagation()}
            />
          </div>
        </div>
        <div
          ref={scrollRef}
          className="max-h-[280px] overflow-auto"
        >
          {filteredUsers.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No users found.
            </div>
          ) : (
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const user = filteredUsers[virtualRow.index]
                const userId = user._id || user.id || ''
                return (
                  <div
                    key={userId}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <button
                      type="button"
                      className={cn(
                        'w-full px-3 py-2.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                        value === userId && 'bg-accent'
                      )}
                      onClick={() => handleSelect(userId)}
                    >
                      <div className="font-medium truncate">
                        {user.name} {user.surname || ''}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </div>
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
